import type { IFilter, ListingsAPIRequestBody } from '@noths/polaris-client-listings-api';
import {
  ListingsRequestAndFilterBuilder,
  ListingsRequestBuilder,
  ListingsRequestFacetBuilder,
  ListingsRequestFacetIntervalBuilder,
  ListingsRequestOrFilterBuilder,
  ListingsRequestRangeFilterBuilder,
  ListingsRequestTextFilterBuilder,
} from '@noths/polaris-client-listings-api';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';

const PAGE_SIZE = 120;

const buildRangeFilter = (facetKey: string, value: string): ListingsRequestRangeFilterBuilder => {
  const [min, max] = value.split('-').map(Number);
  const filter = new ListingsRequestRangeFilterBuilder(facetKey);

  if (!isNaN(min)) {
    filter.min(min);
  }
  if (!isNaN(max) && max !== 0) {
    filter.max(max);
  }

  return filter;
};

export const adaptStoreToRequestBody = (state: ReduxApplicationState): ListingsAPIRequestBody => {
  const { productsQuery, userConfiguration } = state;
  const page = productsQuery.page ?? 1;
  const listingsRequestBody = new ListingsRequestBuilder()
    .currency(userConfiguration.currencyCode)
    .pageSize(PAGE_SIZE)
    .offset(PAGE_SIZE * (page - 1));

  if (productsQuery.searchTerm) {
    listingsRequestBody.searchTerm(productsQuery.searchTerm);
  }

  if (productsQuery.pageCategories) {
    listingsRequestBody.pageCategories(productsQuery.pageCategories);
  }

  const facetsWithActiveOptions = (productsQuery.facets || []).filter((facet) => {
    return facet.options.some((option) => option.active);
  });

  const rootFilters: IFilter[] = [];

  if (productsQuery.canonicalFilters) {
    if ('and' in productsQuery.canonicalFilters) {
      productsQuery.canonicalFilters.and.forEach((filter) => {
        if ('text' in filter) {
          const textFilter = new ListingsRequestTextFilterBuilder(filter.key, filter.text);
          rootFilters.push(textFilter.build());
        }
      });
    }
  }

  if (productsQuery.facets) {
    const facetsToInclude = productsQuery.facets.filter((facet) => {
      return !(facet.valueType === 'numeric' && facet.options.length === 0);
    });

    const facetSpecs = facetsToInclude.map((facet) => {
      const facetBuilder = new ListingsRequestFacetBuilder(facet.key);

      if (facet.valueType === 'numeric') {
        const intervals = facet.options.map((option) => {
          const [min, max] = option.value.split('-').map(Number);
          const intervalBuilder = new ListingsRequestFacetIntervalBuilder();

          if (!isNaN(min)) {
            intervalBuilder.min(min);
          }
          if (!isNaN(max) && max !== 0) {
            intervalBuilder.max(max);
          }

          return intervalBuilder.build();
        });
        facetBuilder.intervals(intervals);
      }

      facetBuilder.excludedFilterKeys(facet.key);

      return facetBuilder.build();
    });

    listingsRequestBody.facetSpecs(facetSpecs);

    if (facetsWithActiveOptions.length > 0) {
      facetsWithActiveOptions.forEach((facet) => {
        if (facet.valueType === 'numeric') {
          const activeOptions = facet.options.filter((option) => option.active);

          if (activeOptions.length > 1) {
            const orFilter = new ListingsRequestOrFilterBuilder();
            const rangeFilters = activeOptions.map((option) => {
              return buildRangeFilter(facet.key, option.value).build();
            });
            orFilter.filters(rangeFilters);

            rootFilters.push(orFilter.build());
          } else {
            const rangeFilter = buildRangeFilter(facet.key, activeOptions[0].value);

            rootFilters.push(rangeFilter.build());
          }
        } else {
          const textFilter = new ListingsRequestTextFilterBuilder(
            facet.key,
            facet.options.filter((option) => option.active).map((option) => String(option.value)),
          );

          rootFilters.push(textFilter.build());
        }
      });
    }
  }

  if (rootFilters.length > 0) {
    const rootFilter = new ListingsRequestAndFilterBuilder().filters(rootFilters);
    listingsRequestBody.filter(rootFilter.build());
  }

  const activeSortOption = (productsQuery.sortOptions || []).find((option) => option.active);

  if (activeSortOption) {
    if (activeSortOption.key) {
      listingsRequestBody.sortBy(activeSortOption.key, activeSortOption.descending);
    }
  }

  return listingsRequestBody.build();
};
